import React from 'react';
import { FiEdit2, FiCheck, FiXCircle, FiClock, FiRefreshCw, FiSend, FiImage, FiRepeat, FiUser, FiBriefcase } from 'react-icons/fi';
import PostStatusBadge from './PostStatusBadge';
import PlatformBadge from './PlatformBadge';
import { formatPstShort } from './SchedulePicker';
import { EDITABLE_STATUSES, POST_STATUSES } from '../../services/SocialPostService';

function getSnippet(content, maxLen = 120) {
  if (!content) return '';
  const text = content.replace(/\n+/g, ' ').trim();
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

const ACCOUNT_STYLES = {
  personal: { icon: FiUser, label: 'Personal', cls: 'bg-violet-50 text-violet-600' },
  business: { icon: FiBriefcase, label: 'Business', cls: 'bg-indigo-50 text-indigo-600' },
};

export default function PostCard({
  post,
  onEdit,
  onApprove,
  onQueue,
  onCancel,
  onRetry,
  onClick,
  compact = false,
  className = '',
}) {
  if (!post) return null;

  const { _id, platform, account, content, media_url, status, scheduled_at, failure_reason, post_type, repost_config } = post;
  const isEditable = EDITABLE_STATUSES.has(status);
  const isFailed = status === POST_STATUSES.failed;
  const isRepost = post_type === 'repost';
  const acct = ACCOUNT_STYLES[account] || ACCOUNT_STYLES.business;
  const AcctIcon = acct.icon;

  const handleAction = (e, fn) => { e.stopPropagation(); fn?.(_id); };

  return (
    <div
      onClick={() => onClick?.(post)}
      className={`
        group bg-white rounded-2xl border transition-all duration-200 cursor-pointer
        hover:shadow-md hover:-translate-y-0.5
        ${isFailed ? 'border-red-200 ring-1 ring-red-100' : 'border-zinc-200 hover:border-zinc-300'}
        ${compact ? 'p-3' : 'p-4'}
        ${className}
      `}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <PlatformBadge platform={platform} size="xs" showLabel={!compact} />
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium ${acct.cls}`}>
            <AcctIcon className="w-3 h-3" />
            {!compact && acct.label}
          </span>
          <PostStatusBadge status={status} size={compact ? 'xs' : 'sm'} />
          {isRepost && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[10px] font-medium">
              <FiRepeat className="w-3 h-3" /> Repost
            </span>
          )}
        </div>
        {scheduled_at && (
          <div className="flex items-center gap-1 text-xs text-zinc-400">
            <FiClock className="w-3 h-3" />
            <span>{formatPstShort(scheduled_at)}</span>
          </div>
        )}
      </div>

      {/* Content snippet */}
      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm text-zinc-700 leading-relaxed ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>
            {getSnippet(content, compact ? 80 : 120) || (
              <span className="italic text-zinc-400">No content yet</span>
            )}
          </p>
        </div>
        {media_url && (
          <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-zinc-100 border border-zinc-200">
            <img src={media_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        {!media_url && content && (
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-zinc-50 flex items-center justify-center">
            <FiImage className="w-4 h-4 text-zinc-300" />
          </div>
        )}
      </div>

      {/* Repost indicator */}
      {repost_config?.enabled && !isRepost && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-500 bg-zinc-50 px-2 py-1 rounded-lg">
          <FiRepeat className="w-3 h-3 text-indigo-400" />
          <span>
            <span className="capitalize font-medium">{account}</span>
            {' → '}
            <span className="capitalize font-medium">{repost_config.target_account}</span>
            {repost_config.delay_minutes
              ? ` (${repost_config.delay_minutes >= 60 ? `${repost_config.delay_minutes / 60}h` : `${repost_config.delay_minutes}m`} delay)`
              : ' (immediately)'}
          </span>
        </div>
      )}

      {isFailed && failure_reason && (
        <p className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-lg truncate">{failure_reason}</p>
      )}

      {/* Quick actions */}
      <div className="flex items-center gap-1 mt-3 pt-2.5 border-t border-zinc-100 opacity-0 group-hover:opacity-100 transition-opacity">
        {isEditable && (
          <button onClick={(e) => handleAction(e, onEdit)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-zinc-600 hover:bg-zinc-100 transition-colors">
            <FiEdit2 className="w-3 h-3" /> Edit
          </button>
        )}
        {status === POST_STATUSES.pending_approval && (
          <button onClick={(e) => handleAction(e, onApprove)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-emerald-600 hover:bg-emerald-50 transition-colors">
            <FiCheck className="w-3 h-3" /> Approve
          </button>
        )}
        {status === POST_STATUSES.approved && (
          <button onClick={(e) => handleAction(e, onQueue)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors">
            <FiSend className="w-3 h-3" /> Queue
          </button>
        )}
        {status === POST_STATUSES.queued && (
          <button onClick={(e) => handleAction(e, onCancel)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors">
            <FiXCircle className="w-3 h-3" /> Cancel
          </button>
        )}
        {isFailed && (
          <button onClick={(e) => handleAction(e, onRetry)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-amber-600 hover:bg-amber-50 transition-colors">
            <FiRefreshCw className="w-3 h-3" /> Retry
          </button>
        )}
      </div>
    </div>
  );
}
